import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Row, Button, Typography, Select, Space, Form } from 'antd';
import type { InputRef } from 'antd';
import Video, { Room } from 'twilio-video';
import { MdScreenShare, MdStopScreenShare, MdVideocam, MdVideocamOff, MdFullscreen, MdSettings } from 'react-icons/md';
import { useQueryState, parseAsBoolean } from '@lib/queryState';
import useMediaQuery from 'vl-common/src/hooks/useMediaQuery';
import { OptionsModal } from 'vl-common/src/components/Modals';
import { VideoMenuButton } from './VideoMenuButton';
import DynamicOnOffMicrophoneIcon from './DynamicOnOffMicrophoneIcon';
import { LocalTrackPublication } from 'twilio-video/tsdef/LocalTrackPublication';
import MicrophoneCallout from './MicrophoneCallout';
import { useTranslation } from 'react-i18next';
import { saveCameraDeviceId } from '@lib/cameraPreference';
import { NO_BACKGROUND, videoBackground, videoBackgrounds } from '@lib/videoBackgrounds/backgrounds';
import useVideoBackground from '@hooks/useVideoBackground';
import useMyRole from '@hooks/useMyRole';
import useRuntimeConfig from '@vl-core/useRuntimeConfig';

const { Option } = Select;

const getPublishedTracks = (tracks: any) =>
  Array.from(tracks.values()).map((trackPublication: any) => trackPublication.track);

function usePublishingTracks(room: Room) {
  const [tracks, setTracks] = useState(() => ({
    audio: room.localParticipant.audioTracks.size !== 0,
    video: room.localParticipant.videoTracks.size !== 0,
    data: room.localParticipant.dataTracks.size !== 0
  }));

  useEffect(() => {
    if (!room) {
      setTracks({ audio: false, video: false, data: false });
      return () => {};
    }

    setTracks({
      audio: room.localParticipant.audioTracks.size !== 0,
      video: room.localParticipant.videoTracks.size !== 0,
      data: room.localParticipant.dataTracks.size !== 0
    });

    const handleTrackPublished = (publication: LocalTrackPublication) =>
      setTracks((previously) => ({ ...previously, [publication.kind]: true }));

    const handleTrackUnpublished = (publication: LocalTrackPublication) =>
      setTracks((previously) => ({ ...previously, [publication.kind]: false }));

    room.localParticipant.on('trackPublished', handleTrackPublished);
    room.localParticipant.on('trackUnpublished', handleTrackUnpublished);

    return () => {
      room.localParticipant.off('trackPublished', handleTrackPublished);
      room.localParticipant.off('trackUnpublished', handleTrackUnpublished);
    };
  }, [room, setTracks]);

  return { ...tracks };
}

const VideoMenu = ({
  room,
  isReconnecting,
  isPoppedOut,
  onRequestFullScreen = (_) => {},
  onRequestPopout = () => {},
  fullScreen
}: {
  room: Room;
  isReconnecting: boolean;
  isPoppedOut: boolean;
  onRequestFullScreen: (arg0: number) => void;
  onRequestPopout: () => void;
  fullScreen: boolean;
}) => {
  const { t } = useTranslation('common');
  const { audio: hasAudioTrack, video: hasVideoTrack } = usePublishingTracks(room);
  const isScreenShareSupported = navigator?.mediaDevices?.getDisplayMedia;
  const [isSharingScreen, setSharingScreen] = useState(false);
  const [isAudioEnabled, setAudioEnabled] = useQueryState('audio-enabled', parseAsBoolean.withDefault(true));
  const [isVideoEnabled, setVideoEnabled] = useQueryState('video-enabled', parseAsBoolean.withDefault(true));
  const [showMoreSettings, setShowMoreSettings] = useState(false);
  const [mediaDevices, setMediaDevices] = useState<MediaDeviceInfo[]>([]);
  const [videoDevice, setVideoDevice] = useState();
  const [screenShareStream, setScreenShareStream] = useState();

  const { VIDEO_BACKGROUNDS_ENABLED } = useRuntimeConfig();
  const { backgroundId, isLoading: isBackgroundLoading, setBackground } = useVideoBackground();
  const role = useMyRole();
  const showBackgroundPicker = VIDEO_BACKGROUNDS_ENABLED && role === 'clinician';

  const settingsFirstInputRef = useRef<InputRef>(null);

  const handleSettingsAfterOpenChange = (showMoreSettings: boolean) => {
    if (showMoreSettings && settingsFirstInputRef.current) {
      settingsFirstInputRef.current.focus();
    }
  };

  const [currentCamera, setCurrentCamera] = useState<string | undefined>(() => {
    const publications: any[] = Array.from(room?.localParticipant?.videoTracks.values() ?? []);
    const track = publications[0]?.track;
    return track?.mediaStreamTrack?.getSettings()?.deviceId ?? '';
  });

  const [currentMic, setCurrentMic] = useState<string | undefined>(() => {
    const publications: any[] = Array.from(room?.localParticipant?.audioTracks.values() ?? []);
    const track = publications[0]?.track;
    return track?.mediaStreamTrack?.getSettings()?.deviceId ?? '';
  });

  const isClinicianTestCall = new URLSearchParams(globalThis.location.search)?.has('clinicianTestCall');
  const isPatientTestCall = globalThis.location.pathname.endsWith('/test-call/');

  const toggleTracks = (tracks, enabled, isVideo) => {
    // Only toggle video track if the name is not screenshare
    // Prevents toggling of screenshare at wrong time
    getPublishedTracks(tracks).forEach((track) => {
      if (!isVideo || (isVideo && track.name !== 'screenshare')) {
        return enabled ? track.disable() : track.enable();
      }
      return track;
    });
  };

  const toggleMute = useCallback(() => {
    toggleTracks(room.localParticipant.audioTracks, isAudioEnabled, false);
    setAudioEnabled(!isAudioEnabled);
  }, [isAudioEnabled, room.localParticipant.audioTracks, setAudioEnabled]);

  const toggleVideo = useCallback(() => {
    // Prevent clinician from toggling video whilst screen sharing active
    // This prevents mismatches in state and changes. They must stop screensharing first
    if (isSharingScreen) return;
    toggleTracks(room.localParticipant.videoTracks, isVideoEnabled, true);

    // Turn off background when video is off
    if (isVideoEnabled) videoBackground.suspend();
    else videoBackground.resume();

    setVideoEnabled((prev) => !prev);
  }, [isSharingScreen, isVideoEnabled, room.localParticipant.videoTracks, setVideoEnabled]);

  useEffect(() => {
    if (!isAudioEnabled) {
      toggleTracks(room.localParticipant.audioTracks, true, false);
    }

    if (!isVideoEnabled) {
      toggleTracks(room.localParticipant.videoTracks, true, true);
    }
  }, []);

  useEffect(() => {
    return () => {
      // @ts-ignore
      screenShareStream?.getTracks().forEach((track) => {
        track.stop();
      });
    };
  }, [screenShareStream]);

  const stopTracks = useCallback(
    (tracks) => {
      tracks.forEach((track) => {
        if (track) {
          track.stop();
          if (track.mediaStreamTrack) {
            track.mediaStreamTrack.stop();
          }
        }
      });
      room.localParticipant.unpublishTracks(tracks);
    },
    [room.localParticipant]
  );

  const setVideoDev = useCallback(
    async (deviceId: string, options = {}) => {
      if (!deviceId) return;

      const existingTracks = getPublishedTracks(room.localParticipant.videoTracks);

      try {
        const newTrack = await Video.createLocalVideoTrack({ deviceId: { exact: deviceId } });

        await room.localParticipant.publishTrack(newTrack, options);
        setCurrentCamera(deviceId);
        saveCameraDeviceId(deviceId);

        existingTracks.forEach((track) => {
          if (track) {
            track.stop();
          }
        });
        room.localParticipant.unpublishTracks(existingTracks);
      } catch (error) {
        console.error('[VideoMenu] Failed to switch video device', error);
      }
    },
    [room.localParticipant]
  );

  const setAudioDev = useCallback(
    async (mDev) => {
      if (mDev !== '') {
        const tracks = getPublishedTracks(room.localParticipant.audioTracks);
        stopTracks(tracks);

        const newLocalTrack = await Video.createLocalAudioTrack({
          deviceId: { exact: mDev }
        });

        if (!isAudioEnabled) {
          newLocalTrack.disable();
        }

        setCurrentMic(mDev);
        await room.localParticipant.publishTrack(newLocalTrack);
      }
    },
    [room.localParticipant, stopTracks, isAudioEnabled]
  );

  useEffect(() => {
    const local = room?.localParticipant;
    if (!local) return;

    const getId = (tracks: any) => {
      const publications: any[] = Array.from(tracks.values());
      const track = publications[0]?.track;
      return track?.mediaStreamTrack?.getSettings()?.deviceId;
    };

    const videoId = getId(local.videoTracks);
    const audioId = getId(local.audioTracks);

    if (videoId && currentCamera === '') {
      setCurrentCamera(videoId);
    }
    if (audioId && currentMic === '') {
      setCurrentMic(audioId);
    }
  }, [room, currentCamera, currentMic, hasVideoTrack, hasAudioTrack]);

  // if/when the camera changes recalculate the media devices that will be presented in the device menus
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then(setMediaDevices);
  }, []);

  useEffect(() => {
    if (currentCamera !== '') {
      setSharingScreen(false);
      navigator.mediaDevices?.enumerateDevices().then(setMediaDevices);
    }
  }, [currentCamera, setVideoEnabled]);

  const toggleScreenShare = useCallback(async () => {
    if (isSharingScreen) {
      await setVideoDev(videoDevice ?? '');
      setSharingScreen(false);
      toggleTracks(room.localParticipant.videoTracks, !isVideoEnabled, true);
    } else {
      const videoIter: any = room.localParticipant.videoTracks.values().next().value;
      const videoInput = {
        deviceId: videoIter?.track.mediaStreamTrack.getSettings().deviceId
      };

      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          audio: false,
          video: {
            frameRate: 10,
            height: 1080,
            width: 1920
          }
        });
        const track = stream.getTracks()[0];

        // @ts-ignore
        setVideoDevice(videoInput.deviceId);
        // @ts-ignore
        setScreenShareStream(stream);

        stopTracks(getPublishedTracks(room.localParticipant.videoTracks));
        await room.localParticipant.publishTrack(track, {
          name: 'screen', // Tracks can be named to easily find them later
          priority: 'low' // Priority is set to high by the subscriber when the video track is rendered
        });

        track.onended = async () => {
          await setVideoDev(videoInput.deviceId ?? '');
          toggleTracks(room.localParticipant.videoTracks, !isVideoEnabled, true);
          setSharingScreen(false);
        };

        setSharingScreen(true);
      } catch (e) {
        console.log({ e });
        await setVideoDev(videoInput.deviceId ?? '');
      }
    }
  }, [isSharingScreen, room.localParticipant, setVideoDev, setVideoEnabled, stopTracks, videoDevice]);

  const handleKeyDown = useCallback(
    (event) => {
      switch (event.key) {
        case 'm':
          toggleMute();
          break;
        case 'f':
          onRequestFullScreen(1);
          break;
        case 'v':
          toggleVideo();
          break;
        case 's':
          toggleScreenShare();
          break;
        default:
          break;
      }
    },
    [toggleMute, onRequestFullScreen, toggleVideo, toggleScreenShare]
  );

  useEffect(() => {
    if (isPoppedOut) {
      window.addEventListener('keydown', handleKeyDown);
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown, isPoppedOut]);

  const isTabletOrComputer = useMediaQuery('{ minWidth: 768 }');

  const hasRemoteParticipants = room?.participants?.size > 0;

  return (
    <>
      {isSharingScreen && (
        <Row justify="center" align="middle" className="screenShareBanner" style={{ display: 'none' }}>
          <Typography
            // @ts-ignore
            variant="h6"
          >
            {t('You are sharing your screen')}
          </Typography>
          <Button onClick={() => toggleScreenShare()} data-testid="stop-sharing">
            {t('Stop sharing')}
          </Button>
        </Row>
      )}
      <footer className="videoFooter">
        <Row justify="center">
          <Space size="small">
            <MicrophoneCallout>
              <VideoMenuButton
                title={isAudioEnabled ? 'Mute Audio (m)' : 'Start Audio (m)'}
                disabled={!hasAudioTrack || isReconnecting}
                icon={<DynamicOnOffMicrophoneIcon isAudioEnabled={isAudioEnabled && hasAudioTrack} />}
                onClick={toggleMute}
                data-testid="toggle-mute"
                pulsateOnCallStart
              />
            </MicrophoneCallout>
            <VideoMenuButton
              title={isVideoEnabled ? 'Stop Video (v)' : 'Start Video (v)'}
              disabled={!hasVideoTrack || isReconnecting || isSharingScreen}
              onClick={toggleVideo}
              data-testid="toggle-video"
              icon={isVideoEnabled ? <MdVideocam /> : <MdVideocamOff />}
            />
            {!isClinicianTestCall && !isPatientTestCall && (
              <VideoMenuButton
                title={isSharingScreen ? 'Stop Screensharing (s)' : 'Start Screensharing (s)'}
                disabled={!isScreenShareSupported || isReconnecting}
                onClick={toggleScreenShare}
                data-testid="toggle-screenshare"
                icon={isSharingScreen ? <MdStopScreenShare /> : <MdScreenShare />}
              />
            )}
            {!isPoppedOut && isTabletOrComputer && hasRemoteParticipants && (
              <VideoMenuButton
                title="Full Screen (f)"
                onClick={() => onRequestFullScreen(1)}
                icon={<MdFullscreen />}
                data-testid="full-screen"
              />
            )}
            <VideoMenuButton
              title="Call settings (e.g. device select)"
              onClick={() => {
                if (fullScreen) onRequestFullScreen(1);
                setShowMoreSettings(!showMoreSettings);
              }}
              data-testid="device-selection"
              icon={<MdSettings />}
            />
          </Space>
        </Row>
      </footer>

      <OptionsModal
        open={showMoreSettings}
        closable={false}
        title="Change audio and video input devices"
        onOk={() => setShowMoreSettings(false)}
        cancelButtonProps={{ style: { display: 'none' } }}
        afterOpenChange={handleSettingsAfterOpenChange}
      >
        <Form className="video-controls">
          <Form.Item label={t('Video Device')} htmlFor="video-device">
            <Select
              id="video-device"
              ref={settingsFirstInputRef}
              onChange={(value: string) => setVideoDev(value)}
              value={currentCamera}
              data-testid="video-device-select"
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              aria-readonly="true"
              aria-autocomplete="none"
            >
              {mediaDevices
                ?.filter(({ kind }) => kind === 'videoinput')
                .map(({ deviceId, label }) => (
                  <Option key={deviceId} value={deviceId}>
                    {label}
                  </Option>
                ))}
            </Select>
          </Form.Item>
          <Form.Item label={t('Audio Device')} htmlFor="audio-device">
            <Select
              id="audio-device"
              onChange={(value) => setAudioDev(value)}
              value={currentMic}
              data-testid="audio-device-select"
              getPopupContainer={(triggerNode) => triggerNode.parentElement}
              aria-readonly="true"
              aria-autocomplete="none"
            >
              {mediaDevices
                ?.filter(({ kind }) => kind === 'audioinput')
                .map(({ deviceId, label }) => (
                  <Option key={deviceId} value={deviceId}>
                    {label}
                  </Option>
                ))}
            </Select>
          </Form.Item>

          {showBackgroundPicker && (
            <Form.Item label={t('Video Background')} htmlFor="video-background">
              <Select
                id="video-background"
                onChange={setBackground}
                value={backgroundId}
                loading={isBackgroundLoading}
                disabled={isBackgroundLoading}
                data-testid="video-background-select"
                getPopupContainer={(triggerNode) => triggerNode.parentElement}
                virtual={false}
                aria-readonly="true"
                aria-autocomplete="none"
              >
                <Option value={NO_BACKGROUND}>{t('None')}</Option>
                {videoBackgrounds.map(({ id, label }) => (
                  <Option key={id} value={id}>
                    {label}
                  </Option>
                ))}
              </Select>
            </Form.Item>
          )}
        </Form>
      </OptionsModal>
    </>
  );
};

export default VideoMenu;
